import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { ball, box, leaf, mesh, tube } from './art';
import type { InsectSpeech } from './friendlyInsects';

export const FOUNTAIN = { x: 216, z: 62 };
export const SPOUT = { x: 203, z: 78 };
export const STEPS = [207, 210, 213, 216, 219, 222, 225].map(x => ({ x, z: 68 }));
export function cornersReserved(x: number, z: number): boolean {
  return (x >= 194 && x <= 238 && z >= 48 && z <= 92) || (x >= 290 && x <= 334 && z >= 2 && z <= 46);
}
export class GardenCorners {
  readonly fountain = new THREE.Group();
  readonly artist = new THREE.Group();
  readonly stoneModels: THREE.Mesh[] = [];
  readonly jet = new THREE.Group();
  readonly snail = new THREE.Group();
  private readonly ripples: THREE.Mesh[] = [];
  private readonly spout: THREE.Mesh;
  calm: boolean;
  speech: InsectSpeech | undefined;
  constructor(parent: THREE.Group, calm = false) {
    this.calm = calm; parent.add(this.fountain, this.artist);
    const floor = mesh(new THREE.CircleGeometry(18, 64), '#bbc4c1', this.fountain, 216, .015, 62); floor.rotation.x = -Math.PI / 2;
    const rim = mesh(new THREE.TorusGeometry(11.6, .5, 12, 64), '#c8cecc', this.fountain, 216, .55, 62); rim.rotation.x = -Math.PI / 2;
    const water = mesh(new THREE.CircleGeometry(11.1, 64), new THREE.MeshStandardMaterial({ color: '#63b5bd', metalness: .25, roughness: .2, transparent: true, opacity: .85 }), this.fountain, 216, .26, 62); water.rotation.x = -Math.PI / 2; water.castShadow = false;
    mesh(new THREE.CylinderGeometry(2.5, 3, 4, 24), '#bdc7c5', this.fountain, 216, 2, 57);
    mesh(new THREE.CylinderGeometry(4, 2.8, 1, 32), '#e0e5df', this.fountain, 216, 4.5, 57);
    mesh(new THREE.CylinderGeometry(.6, 1, 5, 16), '#a9bab7', this.fountain, 216, 7.2, 57);
    mesh(new THREE.SphereGeometry(1.2, 20, 12), '#d4dfd8', this.fountain, 216, 10.2, 57);
    this.fountain.add(this.jet); this.jet.position.set(216, 10.2, 57);
    for (let i = 0; i < 6; i++) {
      const a = i * Math.PI / 3;
      tube(this.jet, [new THREE.Vector3(), new THREE.Vector3(Math.cos(a) * 1.5, 2, Math.sin(a) * 1.5), new THREE.Vector3(Math.cos(a) * 3, -5.3, Math.sin(a) * 3)], .09, '#a9e1e5');
    }
    for (let i = 0; i < 3; i++) { const r = mesh(new THREE.RingGeometry(2 + i * 2, 2.06 + i * 2, 48), new THREE.MeshBasicMaterial({ color: '#d7f0e9', transparent: true, opacity: .5, side: THREE.DoubleSide }), this.fountain, 216, .28, 62); r.rotation.x = -Math.PI / 2; r.castShadow = false; this.ripples.push(r); }
    for (const p of STEPS) { const stone = mesh(new THREE.CylinderGeometry(1.65, 1.85, .45, 12), '#dee1d3', this.fountain, p.x, .42, p.z); this.stoneModels.push(stone); }
    this.spout = mesh(new THREE.CylinderGeometry(.25, .25, 2.8, 12), '#859f98', this.fountain, SPOUT.x, .45, SPOUT.z); this.spout.rotation.z = Math.PI / 2;
    for (let i = 0; i < 8; i++) { const a = i * .8; ball(this.fountain, 216 + Math.cos(a) * 12.8, .2, 62 + Math.sin(a) * 12.8, 1.5, .2, .75, i % 2 ? '#648c69' : '#83a373'); }
    leaf(this.fountain, 205, .1, 82, 3, .9, '#62866b', .6);
    mesh(new THREE.CylinderGeometry(.5, .5, .08, 24), '#d9b66b', this.fountain, 230, .08, 60);
    mesh(new THREE.OctahedronGeometry(.35), '#91d7d9', this.fountain, 225, .95, 68);
    this.snail.position.set(214, .1, 84); this.fountain.add(this.snail);
    ball(this.snail, 0, .2, 0, 1, .23, .4, '#d2bd8e');
    ball(this.snail, .1, .8, 0, .65, .65, .5, '#be8992');
    mesh(new THREE.TorusGeometry(.32, .045, 8, 24), '#eed7b4', this.snail, .1, .8, .48);
    for (const z of [-.22, .22]) { tube(this.snail, [new THREE.Vector3(-.8, .2, z), new THREE.Vector3(-.95, .8, z)], .045, '#c6b684'); ball(this.snail, -.95, .8, z, .09, .09, .09, '#374a40'); }
    this.makeArtist();
    this.sync();
  }
  private makeArtist(): void {
    const ground = mesh(new THREE.PlaneGeometry(40, 40), '#d5d9d2', this.artist, 312, .015, 24); ground.rotation.x = -Math.PI / 2;
    for (const [x, z, color] of [[300, 30, '#cb6680'], [310, 35, '#69a6bb'], [326, 25, '#e8bd57']] as const) {
      const g = new THREE.Group(); g.position.set(x, .15, z); g.rotation.y = .3; this.artist.add(g);
      mesh(new RoundedBoxGeometry(2.5, 1, 5, 3, .3), '#e6e8e1', g, 0, .5, 0);
      box(g, 0, 1.03, 0, 2.4, .03, 2.2, color);
      mesh(new THREE.CylinderGeometry(.42, .42, .7, 12), '#77878a', g, 0, .5, -2.7).rotation.x = Math.PI / 2;
      ball(this.artist, x - 1, .08, z - 4, 1.4, .08, .8, color);
    }
    const palette = mesh(new THREE.CylinderGeometry(3.5, 3.5, .22, 40), '#c8a581', this.artist, 303, .16, 16); palette.scale.z = .72;
    ['#c96680', '#69a9bc', '#e7c25f', '#87ad76'].forEach((color, i) => ball(this.artist, 301 + i * 1.15, .32, 15, .45, .12, .35, color));
    const brush = new THREE.Group(); brush.position.set(320, .4, 24); brush.rotation.y = -.5; this.artist.add(brush);
    mesh(new THREE.CylinderGeometry(.3, .5, 10, 12), '#799d9b', brush).rotation.x = Math.PI / 2;
    mesh(new THREE.CylinderGeometry(.5, .5, 1.5, 12), '#bac6c7', brush, 0, 0, -5.5).rotation.x = Math.PI / 2;
    for (let i = 0; i < 7; i++) tube(brush, [new THREE.Vector3((i - 3) * .13, 0, -6), new THREE.Vector3((i - 3) * .13, -.2, -8)], .06, i % 2 ? '#9eb8c7' : '#6f99aa');
    for (let i = 0; i < 13; i++) { const x = 311 + i * .85; const z = 27 - i * 1.25; for (const side of [-1, 1]) ball(this.artist, x + side * .18, .05, z + side * .23, .14, .035, .2, i < 6 ? '#c96880' : '#6b9fb3'); }
    const paper = new THREE.Group(); paper.position.set(322, .055, 10); paper.rotation.y = .18; this.artist.add(paper);
    box(paper, 0, .02, 0, 5, .04, 4, '#f4f0e6');
    const outline = new THREE.EllipseCurve(0, 0, 1, .7).getPoints(32).map(p => new THREE.Vector3(p.x, .05, p.y));
    tube(paper, outline, .025, '#718581');
    for (const side of [-1, 1]) tube(paper, [new THREE.Vector3(side * .7, .05, -.5), new THREE.Vector3(side * .9, .05, -1.1), new THREE.Vector3(side * .35, .05, -.8)], .025, '#718581');
    for (const side of [-1, 1]) ball(paper, side * .35, .06, -.15, .06, .015, .06, '#718581');
    mesh(new THREE.CylinderGeometry(.45, .45, .3, 12), '#77878a', this.artist, 298, .18, 34);
  }
  canAdjust(x: number, z: number): boolean { return !this.calm && Math.hypot(x - SPOUT.x, z - SPOUT.z) < 2; }
  adjust(x: number, z: number): boolean { if (!this.canAdjust(x, z)) return false; this.calm = true; this.sync(); return true; }
  private sync(): void { this.stoneModels.forEach(m => { m.visible = this.calm; }); this.spout.rotation.y = this.calm ? .7 : 0; }
  onStone(x: number, z: number): boolean { return this.calm && STEPS.some(p => Math.hypot(x - p.x, z - p.z) <= 1.6); }
  heightAt(x: number, z: number): number {
    if (this.onStone(x, z)) return .65;
    if (((x - 303) / 3.5) ** 2 + ((z - 16) / 2.52) ** 2 < 1) return .3;
    if (Math.abs(x - 322) < 2.5 && Math.abs(z - 10) < 2) return .1;
    return 0;
  }
  blocked(x: number, z: number): boolean {
    if (Math.hypot(x - 216, z - 62) < 11.8 && !this.onStone(x, z)) return true;
    if (Math.hypot(x - 216, z - 57) < 3.4) return true;
    return [[300, 30], [310, 35], [326, 25]].some(([a, b]) => Math.abs(x - a) < 1.6 && Math.abs(z - b) < 3.3);
  }
  animate(time: number, x: number, z: number): void {
    this.fountain.visible = Math.hypot(x - 216, z - 62) < 85; this.artist.visible = Math.hypot(x - 312, z - 24) < 85;
    this.jet.scale.y = (this.calm ? .55 : 1) + Math.sin(time * 1.3) * .04;
    this.ripples.forEach((m, i) => m.scale.setScalar(1 + Math.sin(time * .8 + i) * .05));
    this.snail.position.x = 214 + Math.sin(time * .15) * .4;
    this.snail.rotation.y = Math.sin(time * .4) * .08;
    const chats = ['I have been admiring this stone all morning.', 'It has not moved. Very considerate.', 'No rush. The fountain is staying here.'];
    this.speech = Math.hypot(x - 214, z - 84) < 6 && time % 22 < 11.4 ? { name: 'Moss', text: chats[Math.floor((time % 22) / 3.8)], position: this.snail.position.clone().add(new THREE.Vector3(0, 1.8, 0)) } : undefined;
  }
}
